import ApiError, { transportError } from './ApiError';
import { normalizeEnvelope } from './normalize';
import { fallbackMessage } from './messages';
import { ERROR_KIND, HTTP_STATUS, isSuccessCode, kindOf } from './contract';

/**
 * Runtime configuration, filled once from main.js through {@link configureHttp}.
 *
 * This module deliberately imports neither Vuex, nor the router, nor the alert
 * helper. Every application behaviour arrives as an injected callback, which
 * keeps the transport layer testable without a store and stops it from forcing
 * side effects onto its callers.
 *
 * @type {Object}
 */
const settings = {
  /**
   * Base URL of the API.
   *
   * @type {string}
   */
  baseURL: process.env.VUE_APP_API_BASE_URL || '',

  /**
   * Language used when the user has no stored preference. The standardization
   * document leaves this value open, so `'id'` is used because it matches how
   * the portal behaves today.
   *
   * @type {string}
   */
  defaultLanguage: 'id',

  /**
   * Request timeout in milliseconds. Zero means no limit, matching the previous
   * behaviour of the long-running export endpoints.
   *
   * @type {number}
   */
  timeout: 0,

  /**
   * Contract version the client asks for, sent as `X-Api-Version` so the
   * backend can tell old and new consumers apart during the migration.
   *
   * @type {string|null}
   */
  apiVersion: null,

  /**
   * Supplies the CSRF token for each request.
   *
   * @type {Function}
   */
  getToken: () => null,

  /**
   * Supplies the username of the signed-in user, sent as `X-User` so the
   * backend can key its rate limiter per person instead of per IP. The
   * value comes from local storage and is therefore a hint, not proof of
   * identity; the backend still has to verify it against the session.
   *
   * @type {Function|null}
   */
  getUsername: null,

  /**
   * Supplies the active language, either `'id'` or `'en'`.
   *
   * @type {Function|null}
   */
  getLanguage: null,

  /**
   * Called on every 401 response.
   *
   * @type {Function|null}
   */
  onUnauthenticated: null,

  /**
   * Called on every 403 response.
   *
   * @type {Function|null}
   */
  onForbidden: null,

  /**
   * Called on every failure, after the more specific handlers above.
   *
   * @type {Function|null}
   */
  onError: null,
};

/**
 * Configures the shared HTTP layer. Call once while the application boots.
 *
 * @param {Object} [options] - Any subset of the runtime settings.
 * @param {string} [options.baseURL] - Base URL of the API.
 * @param {string} [options.defaultLanguage] - Language used as a fallback.
 * @param {number} [options.timeout] - Request timeout in milliseconds.
 * @param {string|null} [options.apiVersion] - Value for the `X-Api-Version` header.
 * @param {Function} [options.getToken] - Supplies the CSRF token.
 * @param {Function} [options.getUsername] - Supplies the username.
 * @param {Function} [options.getLanguage] - Supplies the active language.
 * @param {Function} [options.onUnauthenticated] - Runs on 401 responses.
 * @param {Function} [options.onForbidden] - Runs on 403 responses.
 * @param {Function} [options.onError] - Runs on every failure.
 * @returns {void}
 */
export const configureHttp = (options = {}) => {
  Object.assign(settings, options);
};

/**
 * Resolves the language to use for the current request.
 *
 * @returns {string} Either `'id'` or `'en'`.
 */
export const currentLanguage = () => {
  const resolved = settings.getLanguage ? settings.getLanguage() : null;
  return resolved === 'en' || resolved === 'id'
    ? resolved
    : settings.defaultLanguage;
};

const ACCEPT_LANGUAGE = { id: 'id-ID', en: 'en-US' };

/**
 * Reports whether a value should be sent as-is rather than serialized.
 *
 * @param {*} value - Candidate request body.
 * @returns {boolean} True for body types the browser already understands.
 */
const isNativeBody = (value) =>
  (typeof FormData !== 'undefined' && value instanceof FormData) ||
  (typeof Blob !== 'undefined' && value instanceof Blob) ||
  (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) ||
  (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams);

/**
 * Reports whether a value is binary response content.
 *
 * @param {*} value - Candidate response body.
 * @returns {boolean} True for Blob and ArrayBuffer payloads.
 */
const isBinary = (value) =>
  (typeof Blob !== 'undefined' && value instanceof Blob) ||
  (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer);

/**
 * Reports whether a blob actually carries a JSON envelope.
 *
 * A download endpoint returns a file on success but an ordinary error envelope
 * on failure, and both arrive as a Blob because the request asked for one.
 * The blob content type is what tells them apart.
 *
 * @param {*} value - Candidate response body.
 * @returns {boolean} True when the blob holds JSON.
 */
const isJsonBlob = (value) =>
  typeof Blob !== 'undefined' &&
  value instanceof Blob &&
  (value.type || '').includes('json');

/**
 * Reads a blob as text.
 *
 * FileReader is used rather than `Blob.text`, which arrived much later in
 * browsers and is absent from the jsdom build these tests run on.
 *
 * @param {Blob} blob - Blob to read.
 * @returns {Promise<string>} The text content, empty when it cannot be read.
 */
const readBlobAsText = (blob) =>
  new Promise((resolve) => {
    if (typeof FileReader === 'undefined') {
      resolve('');
      return;
    }

    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
  });

/**
 * Builds the absolute request URL, appending query parameters when given.
 *
 * Portal endpoints already carry a query string, as in `/index.php?r=api/v1/x`,
 * so the separator is chosen from the URL that is being extended.
 *
 * @param {string} url - Path relative to the base URL, or an absolute URL.
 * @param {Object|null} [params] - Query string parameters.
 * @returns {string} The URL to open.
 */
const buildUrl = (url, params) => {
  const absolute = /^https?:\/\//i.test(url)
    ? url
    : `${settings.baseURL}${url}`;

  if (!params) return absolute;

  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => query.append(`${key}[]`, item));
      return;
    }
    query.append(key, value);
  });

  const serialized = query.toString();
  if (!serialized) return absolute;

  return `${absolute}${absolute.includes('?') ? '&' : '?'}${serialized}`;
};

/**
 * Finds a header by name without regard to case.
 *
 * @param {Object} headers - Header map to search.
 * @param {string} name - Header name to look for.
 * @returns {string|undefined} The matching key, or undefined when absent.
 */
const findHeaderKey = (headers, name) =>
  Object.keys(headers).find(
    (key) => key.toLowerCase() === name.toLowerCase()
  );

/**
 * Builds the outgoing headers for one request.
 *
 * FormData is left without a Content-Type on purpose, so the browser can set it
 * together with the multipart boundary. Methods that carry a body always
 * declare JSON, even when the body is empty, which is what the endpoints have
 * always received.
 *
 * @param {Object} config - Request config.
 * @returns {Object} Headers ready to be applied to the request.
 */
const buildHeaders = (config) => {
  const headers = { ...(config.headers || {}) };
  const method = (config.method || 'get').toUpperCase();
  const carriesBody = method !== 'GET' && method !== 'HEAD';
  const isFormData =
    typeof FormData !== 'undefined' && config.data instanceof FormData;
  const contentTypeKey = findHeaderKey(headers, 'Content-Type');

  if (isFormData) {
    if (contentTypeKey) delete headers[contentTypeKey];
  } else if (!contentTypeKey && (carriesBody || config.data != null)) {
    headers['Content-Type'] = 'application/json';
  }

  headers['Accept-Language'] = ACCEPT_LANGUAGE[currentLanguage()];
  headers['X-Origin-Path'] = window.location.pathname;

  const token = settings.getToken ? settings.getToken() : null;
  if (token) headers['X-CSRF-TOKEN'] = token;

  const username = settings.getUsername ? settings.getUsername() : null;
  if (username) headers['X-User'] = username;

  if (settings.apiVersion) {
    headers['X-Api-Version'] = String(settings.apiVersion);
  }

  return headers;
};

/**
 * Serializes a request body.
 *
 * Store modules historically pass an already stringified JSON payload, so a
 * string is forwarded untouched.
 *
 * @param {*} data - Request body.
 * @returns {*} The value to hand to `send`.
 */
const serializeBody = (data) => {
  if (data === null || data === undefined) return null;
  if (typeof data === 'string') return data;
  if (isNativeBody(data)) return data;
  return JSON.stringify(data);
};

/**
 * Turns the raw header block into a lower-cased map.
 *
 * @param {string} raw - Value returned by `getAllResponseHeaders`.
 * @returns {Object<string, string>} Parsed headers.
 */
const parseHeaders = (raw) => {
  const result = {};
  if (!raw) return result;

  raw
    .trim()
    .split(/[\r\n]+/)
    .forEach((line) => {
      const separator = line.indexOf(':');
      if (separator < 0) return;
      const key = line.slice(0, separator).trim().toLowerCase();
      result[key] = line.slice(separator + 1).trim();
    });

  return result;
};

/**
 * Reads the response body.
 *
 * Binary response types are returned untouched. Text is parsed as JSON when
 * possible and returned as-is otherwise, which keeps a gateway error page from
 * throwing before it can be reported.
 *
 * @param {XMLHttpRequest} xhr - The completed request.
 * @returns {*} The parsed body.
 */
const readBody = (xhr) => {
  const type = xhr.responseType;

  if (type && type !== 'text') return xhr.response;

  const text = typeof xhr.responseText === 'string' ? xhr.responseText : '';
  if (text.trim() === '') return '';

  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
};

/**
 * Reads the correlation ID from the response headers.
 *
 * @param {Object} headers - Parsed response headers.
 * @returns {string|null} The correlation ID, or null when absent.
 */
const readRequestId = (headers) =>
  headers['x-request-id'] ||
  headers['x-correlation-id'] ||
  headers['x-trace-id'] ||
  null;

/**
 * Creates a cancellation token.
 *
 * Used by search-as-you-type inputs so that an in-flight request is dropped as
 * soon as the user types again.
 *
 * @returns {{ token: Object, cancel: Function }} Token for the request config
 *   and the function that cancels the request.
 */
export const createCancelToken = () => {
  const listeners = new Set();

  const token = {
    canceled: false,

    /**
     * Registers a function to run when the token is canceled.
     *
     * @param {Function} listener - Callback that aborts the request.
     * @returns {void}
     */
    subscribe(listener) {
      listeners.add(listener);
    },

    /**
     * Removes a previously registered listener.
     *
     * @param {Function} listener - Callback to drop.
     * @returns {void}
     */
    unsubscribe(listener) {
      listeners.delete(listener);
    },
  };

  const cancel = () => {
    if (token.canceled) return;
    token.canceled = true;
    listeners.forEach((listener) => listener());
    listeners.clear();
  };

  return { token, cancel };
};

/**
 * Performs one request over XMLHttpRequest.
 *
 * XMLHttpRequest is used rather than the Fetch API because it is the only
 * browser transport that reports upload progress, and because it distinguishes
 * a timeout from a caller-initiated cancellation through separate events. Fetch
 * surfaces both as the same abort error.
 *
 * @param {Object} config - Request config.
 * @returns {Promise<{ status: number, data: *, headers: Object }>} The raw
 *   response, before contract normalization.
 * @throws {ApiError} On network failure, timeout, or cancellation.
 */
const send = (config) =>
  new Promise((resolve, reject) => {
    const language = currentLanguage();
    const { cancelToken, signal } = config;

    if (cancelToken?.canceled || signal?.aborted) {
      reject(
        transportError(
          ERROR_KIND.CANCELED,
          fallbackMessage(language, 'canceled')
        )
      );
      return;
    }

    let settled = false;
    let releaseCancellation = () => {};

    /**
     * Settles the promise once and releases the cancellation listeners.
     *
     * @param {Function} settle - Either resolve or reject.
     * @param {*} value - Value to settle with.
     * @returns {void}
     */
    const finish = (settle, value) => {
      if (settled) return;
      settled = true;
      releaseCancellation();
      settle(value);
    };

    try {
      const xhr = new XMLHttpRequest();
      const method = (config.method || 'get').toUpperCase();

      xhr.open(method, buildUrl(config.url, config.params), true);

      const timeout = config.timeout ?? settings.timeout;
      if (timeout) xhr.timeout = timeout;
      if (config.responseType) xhr.responseType = config.responseType;
      if (config.withCredentials) xhr.withCredentials = true;

      const headers = buildHeaders(config);
      Object.keys(headers).forEach((key) => {
        if (headers[key] !== null && headers[key] !== undefined) {
          xhr.setRequestHeader(key, headers[key]);
        }
      });

      if (config.onUploadProgress && xhr.upload) {
        xhr.upload.onprogress = config.onUploadProgress;
      }

      const abort = () => xhr.abort();

      releaseCancellation = () => {
        cancelToken?.unsubscribe(abort);
        signal?.removeEventListener('abort', abort);
      };

      xhr.onload = () =>
        finish(resolve, {
          status: xhr.status,
          data: readBody(xhr),
          headers: parseHeaders(xhr.getAllResponseHeaders()),
        });

      xhr.onerror = () =>
        finish(
          reject,
          transportError(
            ERROR_KIND.NETWORK,
            fallbackMessage(language, 'network')
          )
        );

      xhr.ontimeout = () =>
        finish(
          reject,
          transportError(
            ERROR_KIND.TIMEOUT,
            fallbackMessage(language, 'timeout')
          )
        );

      xhr.onabort = () =>
        finish(
          reject,
          transportError(
            ERROR_KIND.CANCELED,
            fallbackMessage(language, 'canceled')
          )
        );

      cancelToken?.subscribe(abort);
      signal?.addEventListener('abort', abort);

      xhr.send(serializeBody(config.data));
    } catch (error) {
      finish(
        reject,
        transportError(
          ERROR_KIND.UNKNOWN,
          fallbackMessage(language, 'unknown'),
          error
        )
      );
    }
  });

/**
 * Applies the response contract to a raw response.
 *
 * @param {Object} raw - Raw response from {@link send}.
 * @returns {Promise<Object>} The normalized envelope.
 * @throws {ApiError} On any failure reported by the server.
 */
const processResponse = async (raw) => {
  let body = raw.data;

  if (isJsonBlob(body)) {
    const text = await readBlobAsText(body);
    try {
      body = JSON.parse(text);
    } catch (error) {
      body = text;
    }
  }

  const binaryPayload = isBinary(body) ? body : null;

  const envelope = normalizeEnvelope({
    body: binaryPayload ? null : body,
    httpStatus: raw.status,
    requestId: readRequestId(raw.headers),
    language: currentLanguage(),
  });

  if (isSuccessCode(envelope.code)) {
    return binaryPayload ? { ...envelope, data: binaryPayload } : envelope;
  }

  const error = new ApiError({
    code: envelope.code,
    message: envelope.message,
    errors: envelope.errors,
    data: envelope.data,
    detail: envelope.detail,
    requestId: envelope.requestId,
    kind: kindOf(envelope.code),
    legacyCode: envelope.legacyCode,
  });

  if (error.code === HTTP_STATUS.UNAUTHENTICATED && settings.onUnauthenticated) {
    settings.onUnauthenticated(error);
    error.handled = true;
  }

  if (error.code === HTTP_STATUS.FORBIDDEN && settings.onForbidden) {
    settings.onForbidden(error);
    error.handled = true;
  }

  if (settings.onError) settings.onError(error);

  throw error;
};

/**
 * Sends a request and resolves with the normalized envelope.
 *
 * Successful responses, meaning 200 and 204, resolve with the envelope. Every
 * failure rejects with an {@link ApiError}. There is no path where a failure
 * resolves as a success value, so a result can never be ignored by accident.
 *
 * @param {Object} config - Request config.
 * @param {string} [config.method='get'] - HTTP method.
 * @param {string} config.url - Path relative to the base URL.
 * @param {*} [config.data] - Request body.
 * @param {Object} [config.params] - Query string parameters.
 * @param {Object} [config.headers] - Extra headers.
 * @param {string} [config.responseType] - Set to `'blob'` for downloads.
 * @param {number} [config.timeout] - Overrides the configured timeout.
 * @param {Object} [config.cancelToken] - Token from {@link createCancelToken}.
 * @param {AbortSignal} [config.signal] - Native abort signal.
 * @param {Function} [config.onUploadProgress] - Receives upload progress events.
 * @returns {Promise<Object>} The normalized envelope.
 * @throws {ApiError} On any server or transport failure.
 */
export const request = (config) =>
  send(config).then(processResponse, (error) => {
    if (settings.onError) settings.onError(error);
    throw error;
  });

/**
 * Sends a POST request.
 *
 * @param {string} url - Path relative to the base URL.
 * @param {Object|string|FormData} [data] - Request body.
 * @param {Object} [config] - Extra request config.
 * @returns {Promise<Object>} The normalized envelope.
 */
export const post = (url, data = null, config = {}) =>
  request({ ...config, method: 'post', url, data });

/**
 * Sends a GET request.
 *
 * @param {string} url - Path relative to the base URL.
 * @param {Object} [params] - Query string parameters.
 * @param {Object} [config] - Extra request config.
 * @returns {Promise<Object>} The normalized envelope.
 */
export const get = (url, params = null, config = {}) =>
  request({ ...config, method: 'get', url, params });

/**
 * Sends a PUT request.
 *
 * @param {string} url - Path relative to the base URL.
 * @param {Object|string} [data] - Request body.
 * @param {Object} [config] - Extra request config.
 * @returns {Promise<Object>} The normalized envelope.
 */
export const put = (url, data = null, config = {}) =>
  request({ ...config, method: 'put', url, data });

/**
 * Sends a DELETE request.
 *
 * @param {string} url - Path relative to the base URL.
 * @param {Object} [data] - Request body.
 * @param {Object} [config] - Extra request config.
 * @returns {Promise<Object>} The normalized envelope.
 */
export const del = (url, data = null, config = {}) =>
  request({ ...config, method: 'delete', url, data });

/**
 * Uploads a file and reports progress.
 *
 * @param {string} url - Path relative to the base URL.
 * @param {FormData} formData - Payload to upload.
 * @param {Object} [options] - Upload options merged with the request config.
 * @param {Function} [options.onProgress] - Receives progress from 0 to 100.
 * @returns {Promise<Object>} The normalized envelope.
 */
export const upload = (url, formData, options = {}) => {
  const { onProgress, ...config } = options;

  return request({
    ...config,
    method: 'post',
    url,
    data: formData,
    onUploadProgress: onProgress
      ? (event) => {
        if (!event.total) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
      : undefined,
  });
};

/**
 * The shared HTTP client.
 *
 * @type {Object}
 */
const http = {
  configureHttp,
  createCancelToken,
  currentLanguage,
  del,
  get,
  post,
  put,
  request,
  upload,
};

export default http;
